import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import {
  aiToolLinks,
  appName,
  createInitialAnswers,
  optionalContextFields,
  promptQuestions,
} from './promptConfig';
import { generateReadablePrompt } from './promptGeneratorUtils';

const heroCopy =
  'Answer a few questions and get a free personalised AI prompt you can use in ChatGPT, Claude, Gemini, Copilot or any AI tool.';

const trustBadges = ['100% free', 'No sign up', 'No judgement'];

const helperTextByType = {
  checkbox: 'Select all that apply.',
  radio: 'Choose one.',
};

const optionBaseStyles = {
  width: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  border: '1px solid',
  borderColor: 'rgba(35, 48, 68, 0.12)',
  borderRadius: 2.25,
  px: { xs: 1.5, md: 1.75 },
  py: 1.55,
  cursor: 'pointer',
  backgroundColor: 'background.paper',
  minHeight: 72,
  transition: 'border-color 0.16s ease, background-color 0.16s ease, transform 0.16s ease',
  '&:focus-within': {
    outline: '3px solid rgba(71, 130, 218, 0.24)',
    outlineOffset: 2,
  },
};

const selectedOptionStyles = {
  borderColor: 'primary.main',
  backgroundColor: 'rgba(71, 130, 218, 0.09)',
};

const visuallyHiddenInputStyles = {
  position: 'absolute',
  opacity: 0,
  width: 1,
  height: 1,
  overflow: 'hidden',
};

const kitFormOptions = JSON.stringify({
  settings: {
    after_subscribe: {
      action: 'message',
      success_message: 'Success! Now check your email to confirm your subscription.',
      redirect_url: '',
    },
    analytics: {
      google: null,
      fathom: null,
      facebook: null,
      segment: null,
      pinterest: null,
      sparkloop: null,
      googletagmanager: null,
    },
    modal: {
      trigger: 'timer',
      scroll_percentage: null,
      timer: 5,
      devices: 'all',
      show_once_every: 15,
    },
    powered_by: {
      show: true,
      url: 'https://kit.com/features/forms?utm_campaign=poweredby&utm_content=form&utm_medium=referral&utm_source=dynamic',
    },
    recaptcha: {
      enabled: false,
    },
    return_visitor: {
      action: 'show',
      custom_content: '',
    },
    slide_in: {
      display_in: 'bottom_right',
      trigger: 'timer',
      scroll_percentage: null,
      timer: 5,
      devices: 'all',
      show_once_every: 15,
    },
    sticky_bar: {
      display_in: 'top',
      trigger: 'timer',
      scroll_percentage: null,
      timer: 5,
      devices: 'all',
      show_once_every: 15,
    },
  },
  version: '5',
});

const copyTextToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

function KitSignupForm() {
  useEffect(() => {
    if (document.getElementById('kit-embed-script')) return;

    const script = document.createElement('script');
    script.id = 'kit-embed-script';
    script.src = 'https://f.convertkit.com/ckjs/ck.5.js';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (
    <>
      {/* KIT_FORM_EMBED_START */}
      <form
        action="https://app.kit.com/forms/9483016/subscriptions"
        className="seva-form formkit-form"
        method="post"
        data-sv-form="9483016"
        data-uid="993482d20d"
        data-format="inline"
        data-version="5"
        data-options={kitFormOptions}
        min-width="400 500 600 700 800"
      >
        <div data-style="clean">
          <ul className="formkit-alert formkit-alert-error" data-element="errors" data-group="alert" />
          <div data-element="fields" data-stacked="false" className="seva-fields formkit-fields">
            <div className="formkit-field">
              <input
                className="formkit-input"
                aria-label="First Name"
                name="fields[first_name]"
                placeholder="First Name"
                type="text"
                style={{
                  color: 'rgb(0, 0, 0)',
                  borderColor: 'rgb(227, 227, 227)',
                  borderRadius: 4,
                  fontWeight: 400,
                }}
              />
            </div>
            <div className="formkit-field">
              <input
                className="formkit-input"
                name="email_address"
                aria-label="Email Address"
                placeholder="Email Address"
                required
                type="email"
                style={{
                  color: 'rgb(0, 0, 0)',
                  borderColor: 'rgb(227, 227, 227)',
                  borderRadius: 4,
                  fontWeight: 400,
                }}
              />
            </div>
            <button
              data-element="submit"
              className="formkit-submit formkit-submit"
              type="submit"
              style={{
                color: 'rgb(255, 255, 255)',
                backgroundColor: 'rgb(25, 118, 210)',
                borderRadius: 3,
                fontWeight: 400,
              }}
            >
              <div className="formkit-spinner">
                <div />
                <div />
                <div />
              </div>
              <span>Subscribe</span>
            </button>
          </div>
          <div className="formkit-powered-by-convertkit-container">
            <a
              href="https://kit.com/features/forms?utm_campaign=poweredby&utm_content=form&utm_medium=referral&utm_source=dynamic"
              data-element="powered-by"
              className="formkit-powered-by-convertkit"
              data-variant="dark"
              target="_blank"
              rel="nofollow noopener noreferrer"
            >
              Built with Kit
            </a>
          </div>
        </div>
      </form>
      {/* KIT_FORM_EMBED_END */}
    </>
  );
}

function StepHero({ step }) {
  const progressWidth = step === 1 ? '50%' : '100%';

  return (
    <Box
      className="broke-tool-hero"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.18fr) minmax(300px, 0.82fr)' },
        gap: { xs: 3, md: 4 },
        alignItems: 'center',
        px: { xs: 2.25, sm: 3, md: 5 },
        py: { xs: 3, md: 5 },
        border: '1px solid rgba(35, 48, 68, 0.1)',
        borderRadius: { xs: 3, md: 4 },
        overflow: 'hidden',
        background:
          'linear-gradient(135deg, rgba(248, 251, 255, 0.98) 0%, rgba(255, 255, 255, 0.96) 58%, rgba(238, 246, 252, 0.94) 100%)',
      }}
    >
      <Stack spacing={2.25}>
        <Box>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: 'block', letterSpacing: 1.2, fontWeight: 800, mb: 1 }}
          >
            STEP {step} OF 2
          </Typography>
          <Box
            role="progressbar"
            aria-valuenow={step}
            aria-valuemin={1}
            aria-valuemax={2}
            aria-label={`Step ${step} of 2`}
            sx={{
              width: { xs: 150, sm: 190 },
              height: 7,
              borderRadius: 999,
              backgroundColor: 'rgba(71, 130, 218, 0.13)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: progressWidth,
                height: '100%',
                borderRadius: 999,
                background: 'linear-gradient(90deg, #4782da 0%, #69a6ee 100%)',
              }}
            />
          </Box>
        </Box>

        <Box>
          <Typography
            variant="h3"
            sx={{
              maxWidth: 690,
              color: '#233044',
              fontSize: { xs: '2rem', sm: '2.45rem', md: '3rem' },
              fontWeight: 800,
              lineHeight: 1.08,
              mb: 1.5,
            }}
          >
            {appName}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 680, lineHeight: 1.75, fontSize: { xs: '1rem', md: '1.06rem' } }}
          >
            {heroCopy}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {trustBadges.map((badge) => (
            <Box
              className="broke-tool-trust-badge"
              key={badge}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.35,
                py: 0.7,
                borderRadius: 999,
                border: '1px solid rgba(71, 130, 218, 0.16)',
                backgroundColor: 'rgba(255, 255, 255, 0.82)',
                color: '#233044',
                fontSize: '0.88rem',
                fontWeight: 700,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 17, color: '#4782da' }} />
              {badge}
            </Box>
          ))}
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          New to AI prompts?{' '}
          <Box
            component={RouterLink}
            to="/articles/what-are-ai-prompt-generators/"
            sx={{
              color: 'primary.main',
              fontWeight: 700,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Read the guide
          </Box>
        </Typography>
      </Stack>

      <Box
        className="broke-tool-visual-card"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          minHeight: { xs: 230, md: 300 },
          borderRadius: 4,
          border: '1px solid rgba(71, 130, 218, 0.14)',
          backgroundImage:
            'linear-gradient(135deg, rgba(16, 35, 52, 0.58) 0%, rgba(16, 35, 52, 0.2) 100%), url("/images/apps/meadow.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          p: { xs: 2, md: 2.25 },
        }}
      >
        <Typography
          variant="h5"
          sx={{
            position: 'absolute',
            left: { xs: 20, md: 24 },
            right: { xs: 20, md: 24 },
            bottom: { xs: 20, md: 24 },
            zIndex: 1,
            color: '#ffffff',
            fontWeight: 800,
            lineHeight: 1.22,
            textAlign: 'center',
            mx: 'auto',
            maxWidth: 330,
          }}
        >
          A clearer conversation with AI about money starts here.
        </Typography>
      </Box>
    </Box>
  );
}

function ChoiceGroup({ question, value, onChange, index }) {
  const isCheckbox = question.type === 'checkbox';

  const toggleOption = (option) => {
    if (!isCheckbox) {
      onChange(option);
      return;
    }

    const current = Array.isArray(value) ? value : [];
    const next = current.includes(option)
      ? current.filter((item) => item !== option)
      : [...current, option];
    onChange(next);
  };

  return (
    <Box
      className="broke-tool-question-card"
      component="section"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'auto minmax(0, 1fr)' },
        gap: { xs: 2, md: 2.25 },
        p: { xs: 2, sm: 2.5, md: 3 },
        border: '1px solid rgba(35, 48, 68, 0.09)',
        borderRadius: 3,
        backgroundColor: '#ffffff',
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          width: 38,
          height: 38,
          display: 'grid',
          placeItems: 'center',
          borderRadius: '50%',
          backgroundColor: 'rgba(71, 130, 218, 0.1)',
          color: '#2f6fca',
          fontWeight: 800,
          flexShrink: 0,
        }}
      >
        {index + 1}
      </Box>
      <Box>
        <Typography variant="h6" sx={{ color: '#233044', mb: 0.4, fontWeight: 800, lineHeight: 1.25 }}>
          {question.label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.25, fontWeight: 600 }}>
          {helperTextByType[question.type]}
        </Typography>
        <Grid container spacing={1.5}>
          {question.options.map((option) => {
            const selected = isCheckbox
              ? Array.isArray(value) && value.includes(option)
              : value === option;
            return (
              <Grid item xs={12} sm={6} key={option}>
                <Box
                  component="label"
                  className={`broke-tool-option-card${selected ? ' is-selected' : ''}`}
                  sx={{
                    ...optionBaseStyles,
                    ...(selected ? selectedOptionStyles : {}),
                    '&:hover': {
                      borderColor: selected ? 'primary.main' : 'rgba(71, 130, 218, 0.5)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <input
                    type={isCheckbox ? 'checkbox' : 'radio'}
                    name={question.id}
                    checked={selected}
                    onChange={() => toggleOption(option)}
                    style={visuallyHiddenInputStyles}
                  />
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 34,
                      height: 34,
                      display: 'grid',
                      placeItems: 'center',
                      borderRadius: '50%',
                      backgroundColor: selected ? '#4782da' : 'rgba(35, 48, 68, 0.055)',
                      color: selected ? '#ffffff' : 'rgba(35, 48, 68, 0.48)',
                      flexShrink: 0,
                      transition: 'background-color 0.16s ease, color 0.16s ease',
                    }}
                  >
                    <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: '#233044', fontWeight: selected ? 800 : 600, lineHeight: 1.35, flex: 1 }}
                  >
                    {option}
                  </Typography>
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 24,
                      height: 24,
                      display: 'grid',
                      placeItems: 'center',
                      color: selected ? '#4782da' : 'rgba(35, 48, 68, 0.26)',
                      flexShrink: 0,
                    }}
                  >
                    {selected ? <CheckCircleIcon sx={{ fontSize: 23 }} /> : <RadioButtonUncheckedIcon sx={{ fontSize: 23 }} />}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
}

const BrokePromptGeneratorApp = () => {
  const [answers, setAnswers] = useState(createInitialAnswers);
  const [step, setStep] = useState(1);
  const [copiedMessage, setCopiedMessage] = useState('');

  const generatedPrompt = useMemo(() => generateReadablePrompt(answers), [answers]);

  const updateQuestion = (id, value) => {
    setAnswers((current) => ({ ...current, [id]: value }));
  };

  const updateContext = (id, value) => {
    setAnswers((current) => ({
      ...current,
      optionalContext: {
        ...current.optionalContext,
        [id]: value,
      },
    }));
  };

  const handleGenerate = () => {
    setCopiedMessage('');
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCopy = async (text, message) => {
    await copyTextToClipboard(text);
    setCopiedMessage(message);
  };

  return (
    <>
      <Helmet>
        <title>{appName} | Talk About Finance</title>
        <meta
          name="description"
          content="Answer a short guided questionnaire and create a practical AI prompt for reflecting on why money still feels tight."
        />
        <link rel="canonical" href="https://talkabout.finance/apps/i-earn-ok-money-but-still-feel-broke" />
      </Helmet>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
        <Stack className="broke-tool-shell" spacing={{ xs: 2.25, md: 2.75 }}>
          <StepHero step={step} />

          {step === 1 ? (
            <Stack spacing={2.5}>
              {promptQuestions.map((question, index) => (
                <ChoiceGroup
                  key={question.id}
                  question={question}
                  value={answers[question.id]}
                  index={index}
                  onChange={(value) => updateQuestion(question.id, value)}
                />
              ))}

              <Box
                className="broke-tool-question-card broke-tool-context-card"
                component="section"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'auto minmax(0, 1fr)' },
                  gap: { xs: 2, md: 2.25 },
                  p: { xs: 2, sm: 2.5, md: 3 },
                  border: '1px solid rgba(35, 48, 68, 0.09)',
                  borderRadius: 3,
                  backgroundColor: '#ffffff',
                }}
              >
                <Box
                  aria-hidden="true"
                  sx={{
                    width: 38,
                    height: 38,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(71, 130, 218, 0.1)',
                    color: '#2f6fca',
                    fontWeight: 800,
                  }}
                >
                  {promptQuestions.length + 1}
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ color: '#233044', mb: 0.4, fontWeight: 800 }}>
                    Optional financial context
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2.25, fontWeight: 600 }}>
                    Add anything useful. You can leave fields blank.
                  </Typography>
                  <Grid container spacing={2}>
                    {optionalContextFields.map((field) => (
                      <Grid item xs={12} sm={field.multiline ? 12 : 6} key={field.id}>
                        <TextField
                          fullWidth
                          label={field.label}
                          value={answers.optionalContext[field.id]}
                          inputProps={{ inputMode: field.inputMode }}
                          multiline={field.multiline}
                          minRows={field.multiline ? 4 : undefined}
                          onChange={(event) => updateContext(field.id, event.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: '#fbfdff',
                            },
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>

              <Box
                className="broke-tool-next-panel"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'auto minmax(0, 1fr)' },
                  gap: 2,
                  alignItems: 'center',
                  p: { xs: 2, md: 2.5 },
                  border: '1px solid rgba(71, 130, 218, 0.14)',
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(71, 130, 218, 0.09) 0%, rgba(255, 255, 255, 0.9) 100%)',
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: '50%',
                    backgroundColor: '#ffffff',
                    color: '#4782da',
                  }}
                >
                  <ShieldOutlinedIcon />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ color: '#233044', fontWeight: 800, mb: 0.5 }}>
                    Next up
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                    We&apos;ll generate your personalised prompt so you can copy it into your preferred AI tool.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGenerate}
                  sx={{
                    minHeight: 54,
                    px: 3.5,
                    borderRadius: '8px !important',
                    fontWeight: 800,
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  Continue to Step 2
                </Button>
              </Box>
            </Stack>
          ) : (
            <Stack spacing={3}>
              <Box
                className="broke-tool-prompt-preview"
                sx={{
                  border: '1px solid',
                  borderColor: 'rgba(35, 48, 68, 0.09)',
                  borderRadius: 3,
                  overflow: 'hidden',
                  backgroundColor: 'background.paper',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                  sx={{
                    px: { xs: 2, md: 2.5 },
                    py: 2,
                    borderBottom: '1px solid',
                    borderColor: 'rgba(35, 48, 68, 0.08)',
                    backgroundColor: '#ffffff',
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ color: '#233044', fontWeight: 800 }}>
                      Prompt preview
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Scroll through the full prompt below, then copy it when you are ready.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => handleCopy(generatedPrompt, 'Prompt copied.')}
                    sx={{
                      minHeight: 50,
                      borderRadius: '8px !important',
                      fontWeight: 800,
                      flexShrink: 0,
                    }}
                  >
                    Copy prompt
                  </Button>
                </Stack>

                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: { xs: 2, md: 2.5 },
                    maxHeight: 360,
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: '0.92rem',
                    lineHeight: 1.6,
                    color: 'text.primary',
                    backgroundColor: '#fbfdff',
                  }}
                >
                  {generatedPrompt}
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                Copy your prompt, paste it into your preferred AI tool, and adjust anything that does not quite fit your situation.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => setStep(1)}
                  sx={{ minHeight: 50, borderRadius: 2, fontWeight: 800, width: { xs: '100%', sm: 'auto' } }}
                >
                  Back to edit answers
                </Button>
                {copiedMessage && (
                  <Typography variant="body2" color="success.main" sx={{ fontWeight: 700 }}>
                    {copiedMessage}
                  </Typography>
                  )}
              </Stack>

              <Grid container spacing={1.5}>
                {aiToolLinks.map((tool) => (
                  <Grid item xs={12} sm={6} md={3} key={tool.href}>
                    <Button
                      component="a"
                      href={tool.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      fullWidth
                      endIcon={<OpenInNewIcon />}
                      sx={{
                        minHeight: 50,
                        justifyContent: 'space-between',
                        borderRadius: 2,
                        backgroundColor: '#ffffff',
                        fontWeight: 800,
                      }}
                    >
                      {tool.label}
                    </Button>
                  </Grid>
                ))}
              </Grid>

              <Box
                component="section"
                className="broke-tool-email-capture"
                sx={{
                  p: { xs: 2, md: 2.5 },
                  border: '1px solid rgba(71, 130, 218, 0.14)',
                  borderRadius: 3,
                  backgroundColor: '#ffffff',
                }}
              >
                <Typography variant="h6" sx={{ color: '#233044', fontWeight: 800 }}>
                  Want to know when new tools like this are released?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                  I&apos;m building practical calculators, AI-powered financial clarity tools and realistic money resources for people navigating modern financial stress.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                  Join the mailing list for updates and new tools.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  No spam. Unsubscribe anytime.
                </Typography>

                <Box
                  className="broke-tool-kit-form-placeholder"
                  sx={{
                    mt: 2,
                    '& .formkit-form': {
                      maxWidth: 'none',
                      width: '100%',
                      WebkitFontSmoothing: 'antialiased',
                      MozOsxFontSmoothing: 'grayscale',
                    },
                    '& .formkit-form *': {
                      boxSizing: 'border-box',
                    },
                    '& [data-style="clean"]': {
                      width: '100%',
                    },
                    '& .formkit-alert': {
                      width: '100%',
                      listStyle: 'none',
                      m: '0 0 1rem',
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: '#fff',
                      border: '1px solid #e3e3e3',
                      textAlign: 'center',
                    },
                    '& .formkit-alert:empty': {
                      display: 'none',
                    },
                    '& .formkit-fields': {
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr auto' },
                      gap: 1.25,
                      alignItems: 'stretch',
                    },
                    '& .formkit-field': {
                      minWidth: 0,
                    },
                    '& .formkit-input': {
                      width: '100%',
                      height: 48,
                      px: 1.75,
                      py: 1.25,
                      border: '1px solid #e3e3e3 !important',
                      borderRadius: '4px !important',
                      color: '#000000 !important',
                      bgcolor: '#fff',
                      font: 'inherit',
                      fontWeight: '400 !important',
                      outline: 'none',
                      transition: 'border-color 0.18s ease',
                    },
                    '& .formkit-input:focus': {
                      borderColor: '#1976d2 !important',
                    },
                    '& .formkit-submit': {
                      minHeight: 48,
                      px: { xs: 0, sm: 0 },
                      border: 0,
                      borderRadius: '3px !important',
                      bgcolor: '#1976d2 !important',
                      color: '#fff !important',
                      cursor: 'pointer',
                      font: 'inherit',
                      fontWeight: '400 !important',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                    },
                    '& .formkit-submit > span': {
                      display: 'block',
                      px: 2.5,
                      py: 1.35,
                    },
                    '& .formkit-submit:hover, & .formkit-submit:focus': {
                      bgcolor: '#1565c0 !important',
                      outline: 'none',
                    },
                    '& .formkit-spinner': {
                      display: 'none',
                    },
                    '& .formkit-powered-by-convertkit-container': {
                      mt: 1.25,
                      display: 'flex',
                      justifyContent: { xs: 'center', md: 'flex-end' },
                    },
                    '& .formkit-powered-by-convertkit': {
                      color: 'text.secondary',
                      fontSize: '0.76rem',
                      textDecoration: 'none',
                    },
                  }}
                >
                  <KitSignupForm />
                </Box>
              </Box>

            </Stack>
          )}
        </Stack>
      </Container>
    </>
  );
};

export default BrokePromptGeneratorApp;
